import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  GHABlockedIdentity,
  GHABlockedIdentityDetails,
  ListGHABlockedIdentitiesFilters,
  bulkUpsertGHABlockedIdentities,
  createGHABlockedIdentity,
  deleteGHABlockedIdentity,
  listGHABlockedIdentities,
  updateGHABlockedIdentity,
} from '~/services/ConnectionGoogleService';

export const ghaBlockedIdentityQueryKeys = {
  all: ['ghaBlockedIdentities'] as const,
  lists: () => [...ghaBlockedIdentityQueryKeys.all, 'list'] as const,
  list: (filters: ListGHABlockedIdentitiesFilters) => [...ghaBlockedIdentityQueryKeys.lists(), filters] as const,
};

export function useGHABlockedIdentities(filters: ListGHABlockedIdentitiesFilters = {}) {
  return useQuery({
    queryKey: ghaBlockedIdentityQueryKeys.list(filters),
    queryFn: async ({ signal }) => {
      const response = await listGHABlockedIdentities(filters, { signal });
      return { result: response.result, total: response.total };
    },
    // Keep the current rows visible while a filter or page change refetches.
    placeholderData: keepPreviousData,
  });
}

export function useUpsertGHABlockedIdentity() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    // An edit saves by id; a new block sends the address, which the API canonicalises.
    mutationFn: async ({ id, value, input }: { id?: string; value: string; input: GHABlockedIdentityDetails }) =>
      id == null ? await createGHABlockedIdentity('email', value, input) : await updateGHABlockedIdentity(id, input),
    onSuccess: (response, { id }) => {
      queryClient.invalidateQueries({ queryKey: ghaBlockedIdentityQueryKeys.lists() });
      // The stored value is canonical, which may differ from what was typed.
      enqueueSnackbar(`${id == null ? 'Blocked' : 'Updated'} ${response.result.value}`, { variant: 'success' });
    },
  });
}

export function useDeleteGHABlockedIdentity() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (identity: GHABlockedIdentity) => await deleteGHABlockedIdentity(identity.id),
    onSuccess: (_response, identity) => {
      queryClient.invalidateQueries({ queryKey: ghaBlockedIdentityQueryKeys.lists() });
      enqueueSnackbar(`Unblocked ${identity.value}`, { variant: 'success' });
    },
    onError: (error: Error) => {
      enqueueSnackbar(`Failed to delete: ${error.message}`, { variant: 'error' });
    },
  });
}

export function useBulkUpsertGHABlockedIdentities() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: async (file: File) => await bulkUpsertGHABlockedIdentities(file),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ghaBlockedIdentityQueryKeys.lists() });
      enqueueSnackbar(`Uploaded: ${response.result.created} created, ${response.result.updated} updated`, {
        variant: 'success',
      });
    },
  });
}
